/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import type {RouteObject} from 'react-router-dom'
import i18next from '@canvas/i18next'

type RouteModule = Awaited<ReturnType<NonNullable<RouteObject['lazy']>>>

// router.tsx's equivalent of loadBundle.ts's preloadBundleNamespaces await --
// react-router's lazy() targets have no bundle-name key to look up there, so
// this calls i18next directly. Must await (not fire-and-forget): CFA-1268
// found that letting a namespace load race a bundle's own render let a
// non-hook i18next.t() call (e.g. AccountTabContainer's inline usage) render
// with stale/English text and never re-render, since only useTranslation()
// subscribes to the store's 'added' event. Promise.all keeps both fetches
// starting in the same tick -- no waterfall -- while still resolving lazy()
// only once the namespace is actually loaded.
export function routeLazy<T extends RouteModule>(
  importFn: () => Promise<T>,
  namespaces?: string | string[],
): () => Promise<T> {
  if (!namespaces) return importFn
  return async () => {
    const [, mod] = await Promise.all([i18next.loadNamespaces(namespaces), importFn()])
    return mod
  }
}
